import React from 'react';
import { AlertTriangle, RotateCcw, Search, UserSearch } from 'lucide-react';
import Select from '../../Select';
import DatePicker from '../../DatePicker';
import ReportTable from '../shared/ReportTable';
import { formatPatientAge } from '../../../utils/age';
import { clinicToday } from '../../../utils/clinicTime';
import { fmtMoney } from '../shared/chartTheme';
import { CNIC_OF_LABEL, DASH, StatusBadge, fmtDate, human } from './profileFormat';

// The first half of the Patient Profile: find someone by any detail on file. Every field is
// optional and they combine, so "Female, 18 to 40, Area: Chak 51, seen by Dr Sana this year" is one
// search. The form and its results live in the parent, so coming back from a profile finds them as
// they were left.

export const EMPTY_FORM = {
  name: '',
  mrn: '',
  cnic: '',
  phone: '',
  relative: '',
  place: '',
  documentNumber: '',
  gender: '',
  status: '',
  ageFrom: '',
  ageTo: '',
  registeredFrom: '',
  registeredTo: '',
  visitedFrom: '',
  visitedTo: '',
  doctorId: '',
  includeArchived: false,
};

/** True when the form holds at least one real criterion (the archived switch alone is not one). */
export const hasCriteria = (form) =>
  Object.entries(form).some(([k, v]) => k !== 'includeArchived' && String(v ?? '').trim() !== '');

const INPUT =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm';

const GENDERS = [
  { value: '', label: 'Any' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'MALE', label: 'Male' },
  { value: 'OTHER', label: 'Other' },
];

const STATUSES = [
  { value: '', label: 'Any' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'BLACKLISTED', label: 'Blacklisted' },
  { value: 'DECEASED', label: 'Deceased' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
  </div>
);

const buildColumns = (showMoney) => [
  {
    key: 'name',
    label: 'Patient',
    wrap: true,
    render: (r) => (
      <div>
        <div className="font-medium text-gray-900">{r.name}</div>
        {(r.fatherName || r.husbandName) && (
          <div className="text-xs text-gray-500">
            {r.husbandName ? `w/o ${r.husbandName}` : `${r.gender === 'FEMALE' ? 'd/o' : 's/o'} ${r.fatherName}`}
          </div>
        )}
      </div>
    ),
    csv: (r) => r.name,
  },
  { key: 'mrn', label: 'MRN', render: (r) => <span className="font-mono text-xs">{r.mrn}</span> },
  {
    key: 'dateOfBirth',
    label: 'Age / Gender',
    render: (r) => `${formatPatientAge(r) || DASH} · ${human(r.gender)}`,
    csv: (r) => `${formatPatientAge(r) || ''} ${human(r.gender)}`.trim(),
    // Oldest first when sorted "down" reads wrongly for an age column, so sort on the birth date
    // reversed: a larger age sorts higher.
    sortValue: (r) => (r.dateOfBirth ? -Date.parse(`${r.dateOfBirth}T00:00:00`) : null),
  },
  { key: 'phone', label: 'Phone', render: (r) => r.phone || DASH },
  {
    key: 'cnic',
    label: 'CNIC',
    render: (r) =>
      r.cnic ? (
        <span className="font-mono text-xs">
          {r.cnic}
          {CNIC_OF_LABEL[r.cnicOf] && <span className="ml-1 font-sans text-gray-400">({CNIC_OF_LABEL[r.cnicOf]})</span>}
        </span>
      ) : (
        DASH
      ),
    csv: (r) => r.cnic || '',
  },
  { key: 'place', label: 'Area', wrap: true, render: (r) => r.place || DASH },
  {
    key: 'status',
    label: 'Status',
    render: (r) => (
      <span className="inline-flex gap-1">
        <StatusBadge value={r.status} />
        {r.archived && <StatusBadge label="Archived" tone="grey" />}
      </span>
    ),
    csv: (r) => `${human(r.status)}${r.archived ? ' (archived)' : ''}`,
  },
  { key: 'visits', label: 'Visits', align: 'right' },
  { key: 'lastVisit', label: 'Last Visit', render: (r) => fmtDate(r.lastVisit), csv: (r) => r.lastVisit || '' },
  ...(showMoney
    ? [
        {
          key: 'totalPaid',
          label: 'Total Paid',
          align: 'right',
          render: (r) => fmtMoney(r.totalPaid),
          csv: (r) => r.totalPaid,
          sortValue: (r) => Number(r.totalPaid),
        },
      ]
    : []),
];

const PatientSearchPanel = ({ form, setForm, doctors, result, loading, error, onSearch, onClear, onOpen }) => {
  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const onText = (key) => (e) => set(key)(e.target.value);
  const onDigits = (key) => (e) => set(key)(e.target.value.replace(/[^0-9]/g, '').slice(0, 3));
  const today = clinicToday();

  const doctorOptions = [
    { value: '', label: 'Any doctor' },
    ...[...(doctors || [])]
      .map((d) => ({ value: d.id, label: d.fullName || `${d.firstName || ''} ${d.lastName || ''}`.trim() }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const submit = (e) => {
    e.preventDefault();
    onSearch();
  };

  const rows = result?.rows || [];
  const columns = buildColumns(Boolean(result?.access?.money));

  return (
    <div className="space-y-5">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <UserSearch className="w-5 h-5 text-emerald-700" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Find a patient</h3>
            <p className="text-sm text-gray-500">
              Search by any detail on file. Fill in one field or combine several, then open a patient to see everything the
              hospital holds against them.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Name" hint="Any order, e.g. waheed sohail">
            <input className={INPUT} value={form.name} onChange={onText('name')} placeholder="Patient's name" />
          </Field>
          <Field label="MRN" hint="Part of it is enough; old merged MRNs work too">
            <input className={`${INPUT} font-mono`} value={form.mrn} onChange={onText('mrn')} placeholder="PAMH-2609-0001" />
          </Field>
          <Field label="CNIC or B-Form" hint="Also finds a child by a parent's or guardian's CNIC">
            <input className={`${INPUT} font-mono`} value={form.cnic} onChange={onText('cnic')} placeholder="With or without dashes" />
          </Field>
          <Field label="Phone" hint="Any format, e.g. 0300 1234567">
            <input className={INPUT} value={form.phone} onChange={onText('phone')} placeholder="Patient's or guardian's number" />
          </Field>
          <Field label="Relative's name" hint="Father, mother, husband, guardian or who brought them">
            <input className={INPUT} value={form.relative} onChange={onText('relative')} placeholder="Relative's name" />
          </Field>
          <Field label="Address" hint="Area, town or city">
            <input className={INPUT} value={form.place} onChange={onText('place')} placeholder="e.g. Chak 51" />
          </Field>
          <Field label="Rx or invoice number">
            <input className={`${INPUT} font-mono`} value={form.documentNumber} onChange={onText('documentNumber')} placeholder="RX-20260915-0003 or INV-..." />
          </Field>
          <Field label="Gender">
            <Select value={form.gender} onChange={set('gender')} options={GENDERS} placeholder="Any" />
          </Field>
          <Field label="Status">
            <Select value={form.status} onChange={set('status')} options={STATUSES} placeholder="Any" />
          </Field>
          <Field label="Age (years)">
            <div className="flex items-center gap-2">
              <input className={INPUT} inputMode="numeric" value={form.ageFrom} onChange={onDigits('ageFrom')} placeholder="From" aria-label="Age from" />
              <span className="text-gray-400 text-sm">to</span>
              <input className={INPUT} inputMode="numeric" value={form.ageTo} onChange={onDigits('ageTo')} placeholder="To" aria-label="Age to" />
            </div>
          </Field>
          <Field label="Registered between">
            <div className="flex items-center gap-2">
              <DatePicker className="flex-1 min-w-0 text-sm" value={form.registeredFrom} onChange={set('registeredFrom')} max={form.registeredTo || today} placeholder="From" />
              <DatePicker className="flex-1 min-w-0 text-sm" value={form.registeredTo} onChange={set('registeredTo')} min={form.registeredFrom || undefined} max={today} placeholder="To" />
            </div>
          </Field>
          <Field label="Visited between">
            <div className="flex items-center gap-2">
              <DatePicker className="flex-1 min-w-0 text-sm" value={form.visitedFrom} onChange={set('visitedFrom')} max={form.visitedTo || today} placeholder="From" />
              <DatePicker className="flex-1 min-w-0 text-sm" value={form.visitedTo} onChange={set('visitedTo')} min={form.visitedFrom || undefined} max={today} placeholder="To" />
            </div>
          </Field>
          <Field label="Seen by doctor">
            <Select value={form.doctorId} onChange={set('doctorId')} options={doctorOptions} placeholder="Any doctor" />
          </Field>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none pb-2">
              <input
                type="checkbox"
                checked={form.includeArchived}
                onChange={(e) => set('includeArchived')(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              Include archived records
            </label>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="submit"
            disabled={loading || !hasCriteria(form)}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Search className="w-4 h-4" />
            {loading ? 'Searching' : 'Search'}
          </button>
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-700 rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-50"
          >
            <RotateCcw className="w-4 h-4" />
            Clear
          </button>
          {!hasCriteria(form) && <span className="text-xs text-gray-500">Enter at least one detail to search by.</span>}
        </div>
      </form>

      {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg text-sm text-red-800">{error}</div>}

      {result?.truncated && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg text-sm text-amber-900 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            {result.matches} patients match. The first {rows.length}, alphabetically, are shown below. Add another detail to narrow
            the search.
          </span>
        </div>
      )}

      {result && (
        <ReportTable
          title={`${result.matches} patient${result.matches === 1 ? '' : 's'} found`}
          columns={columns}
          rows={rows}
          onRowClick={(r) => onOpen(r.id)}
          csvName={`patient-search-${result.asOf}`}
          emptyText="No patient matches these details. Try fewer fields or part of a name or number."
          footnote={
            rows.length
              ? `Showing ${rows.length} of ${result.matches} patient${result.matches === 1 ? '' : 's'}. Click a patient to open their full profile.`
              : undefined
          }
        />
      )}
    </div>
  );
};

export default PatientSearchPanel;
